<template>
  <v-container fluid class="ilp-root pa-4">

    <!-- ══════════ PAGE HEADER ══════════ -->
    <div class="ilp-header mb-8">
      <div class="ilp-header-icon">
        <v-icon size="28" color="white">mdi-file-table-outline</v-icon>
      </div>
      <div class="ilp-header-text">
        <h1 class="ilp-title">Importar lista de precios</h1>
        <p class="ilp-subtitle">
          Subí un archivo de tu proveedor y mapeá las columnas para actualizar precios automáticamente
        </p>
      </div>
    </div>

    <!-- ══════════ CONTENT CARD ══════════ -->
    <v-card class="ilp-card" outlined>

      <v-window v-model="step" touchless class="ilp-window">

        <!-- ─── Step 1: Configuración ─── -->
        <v-window-item :value="1">
          <div class="ilp-step-body">
            <div class="ilp-step-heading">
              <div class="step-heading-bubble">1</div>
              <div>
                <div class="step-heading-title">Configuración</div>
                <div class="step-heading-sub">Seleccioná el proveedor y el tipo de archivo</div>
              </div>
            </div>
            <div class="ilp-step-content">
              <paso-configuracion ref="paso1" @error="onError" @proveedor-change="paso1ProveedorId = $event" />
            </div>
          </div>
        </v-window-item>

        <!-- ─── Step 2: Mapeo de columnas ─── -->
        <v-window-item :value="2">
          <div class="ilp-step-body">
            <div class="ilp-step-heading">
              <div class="step-heading-bubble">2</div>
              <div>
                <div class="step-heading-title">Mapeo de columnas</div>
                <div class="step-heading-sub">Indicá qué columna de tu archivo corresponde a cada campo</div>
              </div>
            </div>
            <div class="ilp-step-content">
              <paso-mapeo-columnas ref="paso2" :modo="config.modo" @error="onError" />
            </div>
          </div>
        </v-window-item>

        <!-- ─── Step 3: Revisión ─── -->
        <v-window-item :value="3">
          <div class="ilp-step-body ilp-step-body--wide">
            <div class="ilp-step-heading px-8 mb-0">
              <div class="step-heading-bubble step-heading-bubble--success">
                <v-icon small color="white">mdi-magnify</v-icon>
              </div>
              <div>
                <div class="step-heading-title">Revisión y aplicación</div>
                <div class="step-heading-sub">
                  Revisá las coincidencias, ajustá precios y aplicá los cambios
                </div>
              </div>
            </div>
            <div class="ilp-step-content pa-0">
              <paso-revision-aplicacion
                ref="paso3"
                :items="stepDataItems"
                :deposito-id="stepData && stepData.depositoId || null"
                :modo="config.modo"
                @update:cantidadParaAplicar="cantidadParaAplicar = $event"
              />
            </div>
          </div>
        </v-window-item>

      </v-window>

      <!-- ─── Action footer ─── -->
      <v-divider />
      <div class="ilp-footer">
        <div class="ilp-footer-left">
          <v-btn
            v-if="step > 1"
            text
            color="grey darken-1"
            @click="goBack"
          >
            <v-icon left small>mdi-arrow-left</v-icon>
            Atrás
          </v-btn>
        </div>
        <div class="ilp-footer-right">
          <v-btn
            v-if="step === 1"
            color="primary"
            elevation="0"
            style="min-width: 140px"
            :disabled="!paso1ProveedorId"
            @click="avanzarPaso1"
          >
            Continuar
            <v-icon right small>mdi-arrow-right</v-icon>
          </v-btn>
          <v-btn
            v-if="step === 2"
            color="primary"
            elevation="0"
            :loading="loadingPaso2"
            @click="avanzarPaso2"
          >
            Comparar precios
            <v-icon right small>mdi-chart-line</v-icon>
          </v-btn>
          <v-btn
            v-if="step === 3"
            color="success"
            elevation="0"
            :disabled="!cantidadParaAplicar"
            @click="abrirModalConfirmacion"
          >
            <v-icon left small>mdi-check-circle-outline</v-icon>
            Aplicar {{ cantidadParaAplicar ? cantidadParaAplicar + ' cambios' : 'cambios' }}
          </v-btn>
        </div>
      </div>
    </v-card>

    <!-- ══════════ CONFIRMATION DIALOG ══════════ -->
    <v-dialog v-model="modalConfirmar" max-width="440" persistent>
      <v-card>
        <v-card-title class="text-h6 pb-2">
          <v-icon left color="warning">mdi-alert-circle-outline</v-icon>
          Confirmar actualización
        </v-card-title>
        <v-card-text class="pb-2">
          Se van a actualizar los precios de
          <strong>{{ cantidadParaAplicar }} producto{{ cantidadParaAplicar !== 1 ? 's' : '' }}</strong>.
          Esta acción no se puede deshacer.
        </v-card-text>
        <v-card-actions class="px-6 pb-4">
          <v-spacer />
          <v-btn text color="grey darken-1" @click="modalConfirmar = false">Cancelar</v-btn>
          <v-btn color="success" elevation="0" :loading="loadingAplicar" @click="ejecutarAplicar">
            <v-icon left small>mdi-check</v-icon>
            Confirmar
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- ══════════ SNACKBAR ══════════ -->
    <v-snackbar v-model="snackbar" :color="snackbarColor" :timeout="4000" top right>
      <v-icon left small>{{ snackbarColor === 'success' ? 'mdi-check-circle' : 'mdi-alert-circle' }}</v-icon>
      {{ snackbarText }}
      <template v-slot:action="{ attrs }">
        <v-btn icon v-bind="attrs" @click="snackbar = false">
          <v-icon small>mdi-close</v-icon>
        </v-btn>
      </template>
    </v-snackbar>

  </v-container>
</template>

<script>
import PasoConfiguracion from "./PasoConfiguracion.vue";
import PasoMapeoColumnas from "./PasoMapeoColumnas.vue";
import PasoRevisionAplicacion from "./PasoRevisionAplicacion.vue";
import CompararPreciosProveedorProvider from "../../../comparar-precios-proveedor/providers/CompararPreciosProveedorProvider";
import UserConfigurationProvider from "../../../user-configuration/providers/UserConfigurationProvider";

export default {
  name: "ImportarListaPreciosPage",
  components: { PasoConfiguracion, PasoMapeoColumnas, PasoRevisionAplicacion },
  data() {
    return {
      step: 1,
      paso1ProveedorId: null,
      config: { modo: "SIMPLE", proveedorId: null },
      stepData: null,
      stepDataItems: [],
      cantidadParaAplicar: 0,
      loadingPaso2: false,
      loadingAplicar: false,
      modalConfirmar: false,
      snackbar: false,
      snackbarText: "",
      snackbarColor: "success",
    };
  },
  methods: {
    goBack() {
      if (this.step > 1) this.step--;
    },
    avanzarPaso1() {
      if (!this.$refs.paso1.validate()) return;
      this.config = this.$refs.paso1.getData();
      this.step = 2;
    },
    async avanzarPaso2() {
      const rows = this.$refs.paso2.procesar();
      if (!rows) return;

      let depositoId = UserConfigurationProvider.getDepositoIdFromCache();
      if (!depositoId) {
        const res = await UserConfigurationProvider.getUserConfiguration(true);
        depositoId = res?.data?.userConfigurationGet?.depositoIdSeleccionado || null;
      }
      if (!depositoId) {
        this.onError("No se encontró un depósito configurado. Verificá tu configuración.");
        return;
      }

      this.loadingPaso2 = true;
      try {
        const res = await CompararPreciosProveedorProvider.compararPreciosProveedor(
          this.config.proveedorId,
          depositoId,
          rows
        );
        const items = res?.data?.compararPreciosProveedor || [];
        this.stepData = { proveedorId: this.config.proveedorId, depositoId, items };
        this.stepDataItems = items;
        this.cantidadParaAplicar = 0;
        this.step = 3;
      } catch (e) {
        this.onError(e?.graphQLErrors?.[0]?.message || e?.message || "Error al procesar el archivo");
      } finally {
        this.loadingPaso2 = false;
      }
    },
    abrirModalConfirmacion() {
      if (this.cantidadParaAplicar <= 0) return;
      this.modalConfirmar = true;
    },
    async ejecutarAplicar() {
      const paso3 = this.$refs.paso3;
      if (!paso3) return;

      const items = paso3.getItemsParaAplicar();
      if (!items.length) {
        this.onError("No hay ítems seleccionados para aplicar.");
        this.modalConfirmar = false;
        return;
      }

      this.loadingAplicar = true;
      try {
        const res = await CompararPreciosProveedorProvider.aplicarComparacionPreciosProveedor(
          this.stepData.proveedorId,
          this.stepData.depositoId,
          items
        );
        const result = res?.data?.aplicarComparacionPreciosProveedor;
        if (result?.ok) {
          this.modalConfirmar = false;
          this.onSuccess(result.mensaje || `${result.cantidadActualizados} precios actualizados correctamente`);
        } else {
          this.onError(result?.mensaje || "Error al aplicar los cambios");
        }
      } catch (e) {
        this.onError(e?.graphQLErrors?.[0]?.message || e?.message || "Error al aplicar los cambios");
      } finally {
        this.loadingAplicar = false;
      }
    },
    onSuccess(msg) {
      this.snackbarText = msg;
      this.snackbarColor = "success";
      this.snackbar = true;
      setTimeout(() => this.$router.push({ name: "ProductoManagementPage" }), 1200);
    },
    onError(msg) {
      this.snackbarText = msg || "Ocurrió un error";
      this.snackbarColor = "error";
      this.snackbar = true;
    },
  },
};
</script>

<style scoped>
/* ══════════════════════
   ROOT
══════════════════════ */
.ilp-root {
  max-width: 100%;
}

/* ══════════════════════
   HEADER
══════════════════════ */
.ilp-header {
  display: flex;
  align-items: center;
  gap: 16px;
}
.ilp-header-icon {
  width: 52px;
  height: 52px;
  border-radius: 14px;
  background: linear-gradient(135deg, #1976D2 0%, #1565C0 100%);
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  box-shadow: 0 4px 14px rgba(25, 118, 210, 0.32);
}
.ilp-title {
  font-size: 22px;
  font-weight: 700;
  color: #202124;
  margin: 0 0 3px;
  line-height: 1.2;
}
.ilp-subtitle {
  font-size: 13.5px;
  color: #5F6368;
  margin: 0;
}

/* ══════════════════════
   CONTENT CARD
══════════════════════ */
.ilp-card {
  border-radius: 14px !important;
  overflow: hidden;
  border-color: #E8EAED !important;
}
.ilp-window {
  min-height: 420px;
}

/* Step body */
.ilp-step-body {
  padding: 32px 36px 28px;
}
.ilp-step-body--wide {
  max-width: 100%;
  padding: 24px 0 0;
}

/* Step heading */
.ilp-step-heading {
  display: flex;
  align-items: flex-start;
  gap: 12px;
  margin-bottom: 28px;
}
.step-heading-bubble {
  width: 32px;
  height: 32px;
  border-radius: 50%;
  background: #1976D2;
  color: #fff;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 14px;
  font-weight: 700;
  flex-shrink: 0;
  margin-top: 1px;
}
.step-heading-bubble--success { background: #2E7D32; }
.step-heading-title {
  font-size: 16px;
  font-weight: 700;
  color: #202124;
  line-height: 1.3;
}
.step-heading-sub {
  font-size: 12.5px;
  color: #5F6368;
  margin-top: 3px;
}

/* Step content: indent aligned with heading text */
.ilp-step-content { padding-left: 44px; }
.ilp-step-body--wide .ilp-step-content { padding: 0; }

/* ══════════════════════
   FOOTER
══════════════════════ */
.ilp-footer {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 12px 24px;
  background: #FAFAFA;
}
.ilp-footer-left,
.ilp-footer-right {
  display: flex;
  align-items: center;
  gap: 8px;
}
</style>
